import useIsNarrow from "../hooks/useIsNarrow";

export default function ComparablesSection({ comparables, salePropertyBuildingId }) {
  const narrow = useIsNarrow();
  const sectionStyle = {
    backgroundColor: "white",
    border: "none",
    borderRadius: "8px",
    padding: narrow ? "16px 12px" : "24px",
    marginBottom: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  // Only the table scrolls: with the scroller on the card, the heading and the card's own
  // padding slid away with the columns.
  const scrollWrapStyle = {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  };

  const headingStyle = {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #000",
    paddingBottom: "8px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: narrow ? "13px" : "14px",
    // Eight columns. Below this width the cells wrap into an unreadable stack, so past it the
    // wrapper scrolls instead -- a no-op on any card already wider than this.
    minWidth: "720px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "12px 8px",
    borderBottom: "2px solid #e0e0e0",
    fontWeight: "600",
    color: "#333",
    backgroundColor: "#f8f9fa",
  };

  const tdStyle = {
    padding: "12px 8px",
    borderBottom: "1px solid #e0e0e0",
  };

  const getStatusColor = (statusDisplay) => {
    switch (statusDisplay) {
      case 'Rented': return "#4caf50";
      case 'In Contract': return "#2196f3";
      case 'Off Market': return "#9e9e9e";
      case 'Active': return "#ff9800";
      default: return "#ff9800";
    }
  };

  const statusBadgeStyle = (statusDisplay) => ({
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "600",
    backgroundColor: getStatusColor(statusDisplay),
    color: "#fff",
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const buildingBadgeStyle = {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "600",
    backgroundColor: "#2196f3",
    color: "#fff",
    marginLeft: "8px",
  };

  const linkStyle = {
    color: "#000",
    textDecoration: "none",
    fontWeight: "500",
    padding: "4px 8px",
    border: "1px solid #000",
    borderRadius: "4px",
    fontSize: "11px",
    transition: "all 0.3s ease",
  };

  if (!comparables || comparables.length === 0) return null;

  return (
    <div style={sectionStyle}>
      <h2 style={headingStyle}>Top Comparables</h2>
      <div style={scrollWrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>#</th>
            <th style={thStyle}>Address</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Rent</th>
            <th style={thStyle}>Size</th>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Distance</th>
            <th style={thStyle}>Link</th>
          </tr>
        </thead>
        <tbody>
          {comparables.map((comp) => {
            const statusDisplay = comp.status_display || (comp.is_rented ? 'Rented' : 'Active');
            const isSameBuilding = comp.is_same_building || false;
            const displayDate = (statusDisplay === 'Rented' || statusDisplay === 'In Contract')
              ? formatDate(comp.closed_at) || formatDate(comp.listed_at) || "Unknown"
              : formatDate(comp.listed_at) || "Unknown";

            return (
              <tr key={comp.id || comp.rank} style={{ backgroundColor: isSameBuilding ? "#e3f2fd" : "transparent" }}>
                <td style={tdStyle}>{comp.rank}</td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  <strong>{comp.address}</strong>
                  {isSameBuilding && <span style={buildingBadgeStyle}>SAME BUILDING</span>}
                </td>
                <td style={tdStyle}>
                  <span style={statusBadgeStyle(statusDisplay)}>
                    {statusDisplay === 'Rented' ? "Rented" : statusDisplay}
                  </span>
                </td>
                <td style={tdStyle}>${comp.rent?.toLocaleString()}/mo</td>
                <td style={tdStyle}>
                  {comp.sqft} sqft<br />
                  {comp.bedrooms}BR/{comp.bathrooms}BA
                </td>
                <td style={tdStyle}>{displayDate}</td>
                <td style={tdStyle}>{comp.distance_miles} mi</td>
                <td style={tdStyle}>
                  {comp.streeteasy_url || comp.listing_url ? (
                    <a
                      href={comp.streeteasy_url || comp.listing_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = "#000"; e.target.style.color = "#fff"; }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; e.target.style.color = "#000"; }}
                    >View</a>
                  ) : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
